import { match } from 'ts-pattern'
import type { z } from 'zod'
import type { FreeformUploadSessionResponse } from '../../../utils/api-client/api-schemas.ts'
import type { FreeformStatusPhase, FreeformUploadEvent } from '../../../utils/api-client/sse-schemas.ts'
import {
  type CodeExcerpt,
  type FreeformProgressEvent,
  type LocationVerifiedNoteSchema,
  parseFreeformProgress,
} from './freeform-progress-events.ts'

/**
 * One Findings Intake run as its page sees it, rebuilt from the session's event stream alone.
 *
 * The stream replays its whole history on every connection, so the page folds the full event list each
 * time it changes: the same events always yield the same picture, for every viewer and after a reload.
 */

export type LocationState = 'found' | 'verified' | 'rejected'

export type RunLocation = {
  repository: string
  path: string
  line?: number
  state: LocationState
  note?: z.infer<typeof LocationVerifiedNoteSchema>
  reason?: string
  excerpt?: CodeExcerpt
}

export type FindingState = 'extracted' | 'searching' | 'matched' | 'located' | 'verified' | 'rejected' | 'unassociated'

export type RunFinding = {
  number: number
  sourceId?: string
  title: string
  severity: string
  /** The application or section the report filed it under. */
  asset?: string
  state: FindingState
  repository?: string
  locations: RunLocation[]
  /** The last clue the pre-search looked for. */
  clue?: string
  reason?: string
  rationale?: string
}

export type LogTone = 'info' | 'match' | 'verified' | 'rejected' | 'miss'
export type RunLogEntry = { seq: number; ts: string; message: string; tone: LogTone }

export type RunRepoScan = Omit<Extract<FreeformUploadEvent, { type: 'repo_scan' }>, 'type'>

export type FreeformRunState = {
  /** The last progress frame folded in; 0 until the first. */
  seq: number
  /** The first progress frame folded in; above 1 when the platform's replay no longer starts at the run's start. */
  firstSeq: number
  phase: FreeformStatusPhase
  isDone: boolean
  errorReason?: string
  startTs?: string
  ts?: string
  filesRead: { file: string; kind: string }[]
  /** How many findings the report holds, once extraction has counted them. */
  total?: number
  findings: RunFinding[]
  isPresearching: boolean
  batches: number
  searches: number
  copiesIgnored: number
  /** The finding whose code the page shows: the one most recently located or ruled on. */
  focus?: { number: number; repository: string; path: string; line?: number }
  isSummarized: boolean
  repoScans: RunRepoScan[]
  log: RunLogEntry[]
}

export const EMPTY_FREEFORM_RUN: FreeformRunState = {
  seq: 0,
  firstSeq: 0,
  phase: 'parsing',
  isDone: false,
  filesRead: [],
  findings: [],
  isPresearching: false,
  batches: 0,
  searches: 0,
  copiesIgnored: 0,
  isSummarized: false,
  repoScans: [],
  log: [],
}

const stateOf = (finding: RunFinding): FindingState =>
  match(finding)
    .when(
      f => f.state === 'unassociated',
      () => 'unassociated' as const
    )
    .when(
      f => f.locations.some(location => location.state === 'found'),
      () => 'located' as const
    )
    .when(
      f => f.locations.some(location => location.state === 'verified'),
      () => 'verified' as const
    )
    .when(
      f => f.locations.length > 0,
      () => 'rejected' as const
    )
    .when(
      f => f.repository !== undefined,
      () => 'matched' as const
    )
    .otherwise(f => f.state)

const updateFinding = (
  run: FreeformRunState,
  number: number,
  update: (finding: RunFinding) => RunFinding
): FreeformRunState => ({
  ...run,
  findings: run.findings.map(finding => {
    if (finding.number !== number) {
      return finding
    }
    const next = update(finding)
    return { ...next, state: stateOf(next) }
  }),
})

const isSameLocation = (location: RunLocation, other: { repository: string; path: string; line?: number }) =>
  location.repository === other.repository && location.path === other.path && location.line === other.line

/** A location event's place, with analysis-service's `null` line read as no line. */
const placeOf = (detail: { repository: string; path: string; line?: number | null }) => ({
  repository: detail.repository,
  path: detail.path,
  line: detail.line ?? undefined,
})

const settleLocation = (finding: RunFinding, settled: RunLocation): RunFinding => ({
  ...finding,
  locations: finding.locations.some(location => isSameLocation(location, settled))
    ? finding.locations.map(location => (isSameLocation(location, settled) ? settled : location))
    : [...finding.locations, settled],
})

const toneOf = (name: string): LogTone =>
  match(name)
    .with('finding_matched', 'location_found', () => 'match' as const)
    .with('location_verified', () => 'verified' as const)
    .with('location_rejected', () => 'rejected' as const)
    .with('finding_unassociated', () => 'miss' as const)
    .otherwise(() => 'info' as const)

const applyProgress = (run: FreeformRunState, event: FreeformProgressEvent): FreeformRunState =>
  match(event)
    .with({ name: 'file_read' }, ({ detail }) => ({
      ...run,
      filesRead: [...run.filesRead, { file: detail.file, kind: detail.kind }],
    }))
    .with({ name: 'finding_extracted' }, ({ detail }) => ({
      ...run,
      total: detail.total,
      findings: [
        ...run.findings.filter(finding => finding.number !== detail.number),
        {
          number: detail.number,
          sourceId: detail.source_id ?? undefined,
          title: detail.title,
          severity: detail.severity,
          asset: detail.asset ?? undefined,
          state: 'extracted' as const,
          locations: [],
        },
      ].sort((a, b) => a.number - b.number),
    }))
    .with({ name: 'presearch_started' }, () => ({ ...run, isPresearching: true }))
    .with({ name: 'finding_searching' }, ({ detail }) =>
      updateFinding({ ...run, searches: run.searches + 1 }, detail.number, finding => ({
        ...finding,
        clue: detail.clue,
        state: finding.state === 'extracted' ? 'searching' : finding.state,
      }))
    )
    .with({ name: 'copies_ignored' }, ({ detail }) => ({ ...run, copiesIgnored: run.copiesIgnored + detail.count }))
    .with({ name: 'batch_started' }, ({ detail }) =>
      detail.numbers.reduce(
        (next, number) =>
          updateFinding(next, number, finding => ({
            ...finding,
            state: finding.state === 'extracted' ? 'searching' : finding.state,
          })),
        { ...run, batches: run.batches + 1 }
      )
    )
    .with({ name: 'finding_matched' }, ({ detail }) =>
      updateFinding(run, detail.number, finding => ({ ...finding, repository: detail.repository }))
    )
    .with({ name: 'location_found' }, ({ detail }) => {
      const place = placeOf(detail)
      return {
        ...updateFinding(run, detail.number, finding =>
          settleLocation(
            { ...finding, repository: finding.repository ?? place.repository },
            { ...place, state: 'found' }
          )
        ),
        focus: { number: detail.number, ...place },
      }
    })
    .with({ name: 'location_verified' }, ({ detail }) => {
      const place = placeOf(detail)
      return {
        ...updateFinding(run, detail.number, finding =>
          settleLocation(finding, {
            ...place,
            state: 'verified',
            note: detail.note,
            reason: detail.reason,
            excerpt: detail.excerpt ?? undefined,
          })
        ),
        focus: { number: detail.number, ...place },
      }
    })
    .with({ name: 'location_rejected' }, ({ detail }) =>
      updateFinding(run, detail.number, finding =>
        settleLocation(finding, { ...placeOf(detail), state: 'rejected', reason: detail.reason })
      )
    )
    .with({ name: 'finding_unassociated' }, ({ detail }) =>
      updateFinding(run, detail.number, finding => ({
        ...finding,
        state: 'unassociated',
        reason: detail.reason,
        rationale: detail.rationale || undefined,
      }))
    )
    .with({ name: 'association_summary' }, () => ({ ...run, isSummarized: true }))
    .exhaustive()

/** Applies one stream event. A progress frame at or before the run's seq was already applied and is skipped. */
export const fold = (run: FreeformRunState, event: FreeformUploadEvent): FreeformRunState =>
  match(event)
    .with({ type: 'progress' }, frame => {
      if (frame.seq <= run.seq) {
        return run
      }
      const parsed = parseFreeformProgress(frame)
      // A frame this build cannot read still says what happened in words, so it reaches the log.
      return {
        ...(parsed ? applyProgress(run, parsed) : run),
        seq: frame.seq,
        firstSeq: run.firstSeq || frame.seq,
        startTs: run.startTs ?? frame.ts,
        ts: frame.ts,
        log: [...run.log, { seq: frame.seq, ts: frame.ts, message: frame.message, tone: toneOf(frame.name) }],
      }
    })
    .with({ type: 'status' }, ({ phase }) => ({ ...run, phase }))
    .with({ type: 'file' }, () => run)
    .with({ type: 'repo_scan' }, ({ type: _type, ...scan }) => ({
      ...run,
      repoScans: [...run.repoScans.filter(existing => existing.scanId !== scan.scanId), scan],
    }))
    .with({ type: 'unassociated' }, () => run)
    .with({ type: 'done' }, () => ({ ...run, isDone: true }))
    .with({ type: 'error' }, ({ reason }) => ({ ...run, isDone: true, phase: 'failed' as const, errorReason: reason }))
    .exhaustive()

export const foldEvents = (events: readonly FreeformUploadEvent[]): FreeformRunState =>
  events.reduce(fold, EMPTY_FREEFORM_RUN)

/** Whether the stream carried any progress: a run from before progress events, or one whose history was lost, has none. */
export const hasProgress = (run: FreeformRunState) => run.seq > 0

/**
 * Whether the stream replayed the run from its first frame. The platform records every event it relays, but
 * a run whose progress it never received from the start (one from before it recorded events, or one whose
 * job stream it joined late) replays part way through, after findings it then never lists.
 */
export const hasWholeHistory = (run: FreeformRunState) => run.firstSeq === 1

const SETTLED_STATES: ReadonlySet<FindingState> = new Set(['verified', 'rejected', 'unassociated'])

export const isFindingSettled = (finding: RunFinding) => SETTLED_STATES.has(finding.state)

/**
 * How far the run is, 0–100: a sliver while the report is read, then the share of findings that reached
 * their outcome, and the last few percent while the platform creates the scans. No ETA: nothing predicts one.
 */
export const progressPercentOf = (run: FreeformRunState) => {
  // A failed run stays where it stopped, rather than reading as complete.
  if (run.isDone && run.errorReason === undefined) {
    return 100
  }
  if (run.isSummarized) {
    return 95
  }
  if (!run.total) {
    return run.filesRead.length > 0 ? 5 : 0
  }
  const settled = run.findings.filter(isFindingSettled).length
  return Math.round(10 + (85 * settled) / run.total)
}

export type StageKey = 'read' | 'extract' | 'match' | 'locate' | 'verify'
export type StageState = 'pending' | 'active' | 'done'
export type RunStage = { key: StageKey; label: string; state: StageState; count: string }

const located = (run: FreeformRunState) =>
  run.findings.flatMap(finding => finding.locations).filter(location => location.state !== 'rejected')

export const verifiedLocationsOf = (run: FreeformRunState) =>
  run.findings.flatMap(finding => finding.locations).filter(location => location.state === 'verified')

const stage = (key: StageKey, label: string, count: string, isStarted: boolean, isDone: boolean): RunStage => ({
  key,
  label,
  count,
  state: isDone ? 'done' : isStarted ? 'active' : 'pending',
})

/** The five steps from report to scans, each pending, active or done. A finished run has done them all. */
export const stagesOf = (run: FreeformRunState): RunStage[] => {
  const isFinished = run.isDone && run.errorReason === undefined
  const hasSearch = run.isPresearching || run.batches > 0
  const matched = run.findings.filter(finding => finding.repository !== undefined).length
  const locatedCount = located(run).length
  const verifiedCount = verifiedLocationsOf(run).length
  const isAssociated = run.isSummarized || isFinished
  return [
    stage(
      'read',
      'Read files',
      run.filesRead.length > 0 ? `${run.filesRead.length} read` : 'Reading…',
      true,
      run.findings.length > 0 || isAssociated
    ),
    stage(
      'extract',
      'Extract findings',
      run.total !== undefined ? `${run.findings.length} of ${run.total}` : '—',
      run.filesRead.length > 0,
      hasSearch || isAssociated
    ),
    stage('match', 'Match repositories', `${matched} matched`, hasSearch, isAssociated),
    stage('locate', 'Locate code', `${locatedCount} located`, locatedCount > 0, isAssociated),
    stage('verify', 'Verify', `${verifiedCount} confirmed`, verifiedCount > 0, isAssociated),
  ]
}

/** The run's findings, grouped by the repository they were matched to, for the repository cards. */
export const repositoriesOf = (run: FreeformRunState) => {
  const byName = new Map<string, RunFinding[]>()
  for (const finding of run.findings) {
    if (finding.repository === undefined) {
      continue
    }
    byName.set(finding.repository, [...(byName.get(finding.repository) ?? []), finding])
  }
  return [...byName.entries()].map(([name, findings]) => ({
    name,
    findings,
    verified: findings.filter(finding => finding.state === 'verified').length,
    pending: findings.filter(finding => finding.state === 'matched' || finding.state === 'located').length,
  }))
}

/** The finding the code panel shows, and the location of it the panel highlights. */
export const focusOf = (run: FreeformRunState) => {
  const { focus } = run
  if (!focus) {
    return undefined
  }
  const finding = run.findings.find(candidate => candidate.number === focus.number)
  const location = finding?.locations.find(candidate => isSameLocation(candidate, focus))
  return finding && location ? { finding, location } : undefined
}

/** A location as a reader scans it: the file's last two path segments, and its line when it has one. */
/** How a finding is named on screen: the report's own id for it, else its place in the report. */
export const findingLabelOf = (finding: Pick<RunFinding, 'sourceId' | 'number'>) =>
  finding.sourceId ?? `#${finding.number}`

/** Why a failed run stopped, as the platform recorded it. */
export const failureReasonOf = (session: Pick<FreeformUploadSessionResponse, 'failure_reason'>) =>
  session.failure_reason ?? 'The run did not report why.'

export const whereOf = (location: Pick<RunLocation, 'path' | 'line'>): string => {
  const segments = location.path.split('/')
  const short = segments.length > 2 ? `…/${segments.slice(-2).join('/')}` : location.path
  return location.line ? `${short}:${location.line}` : short
}

/** The repository's own name, without its owner. */
export const repositoryShortName = (fullName: string) => fullName.split('/').at(-1) ?? fullName
